import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MagnifyingGlass, Basket, Star, CaretRight, CalendarBlank, CaretDown } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, shortDate, monthShort } from "@/lib/format";
import { Card, Loading, EmptyState, catColor } from "@/components/riq";

export default function Items() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const cur = user?.currency || "ZAR";
  const [items, setItems] = useState(null);       // all-time (for tracked chips + "all" view)
  const [monthItems, setMonthItems] = useState(null); // filtered view (when a month is picked)
  const [months, setMonths] = useState([]);
  const [month, setMonth] = useState("all");
  const [q, setQ] = useState("");

  const load = () => api.get("/items").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => {
    load();
    api.get("/items/months").then((r) => setMonths(r.data)).catch(() => setMonths([]));
  }, []);

  useEffect(() => {
    if (month === "all") { setMonthItems(null); return; }
    setMonthItems(null);
    api.get(`/items?month=${month}`).then((r) => setMonthItems(r.data)).catch(() => setMonthItems([]));
  }, [month]);

  const toggleTrack = async (e, it) => {
    e.stopPropagation();
    try {
      if (it.tracked) await api.delete(`/items/track?name=${encodeURIComponent(it.canonical_name)}`);
      else await api.post("/items/track", { canonical_name: it.canonical_name });
      const flip = (arr) => arr ? arr.map((x) => x.canonical_name === it.canonical_name ? { ...x, tracked: !x.tracked } : x) : arr;
      setItems(flip);
      setMonthItems(flip);
      toast.success(it.tracked ? "Removed from tracked" : "Now tracking this item");
    } catch { toast.error("Could not update"); }
  };

  if (items === null) return <Loading />;

  const source = month === "all" ? items : (monthItems || []);
  const loadingMonth = month !== "all" && monthItems === null;
  const filtered = source.filter((i) => i.canonical_name.toLowerCase().includes(q.toLowerCase()));
  const tracked = items.filter((i) => i.tracked);

  if (items.length === 0) {
    return <EmptyState icon={Basket} title="No items yet" subtitle="Once you scan receipts, we'll automatically build your item history and purchase patterns." />;
  }

  return (
    <div className="space-y-6">
      <h1 className="font-head text-3xl font-bold tracking-tight text-riq-primary">Items</h1>

      {tracked.length > 0 && (
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground mb-3">My Tracked Items</div>
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
            {tracked.map((it) => (
              <button key={it.canonical_name} data-testid={`tracked-chip-${it.canonical_name}`} onClick={() => navigate(`/items/${encodeURIComponent(it.canonical_name)}`)} className="shrink-0 rounded-2xl border border-riq-border bg-white p-4 text-left w-44 hover:-translate-y-0.5 transition-transform">
                <Star size={18} weight="fill" className="text-riq-gold mb-2" />
                <div className="font-medium text-sm truncate">{it.canonical_name}</div>
                <div className="text-xs text-muted-foreground mt-1">{it.purchases}× · {money(it.total_spend, cur)}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input data-testid="item-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search items…" className="w-full bg-white rounded-full border border-riq-border pl-12 pr-4 py-3.5 text-sm outline-none focus:border-riq-primary" />
        </div>
        <div className="relative sm:w-56">
          <CalendarBlank size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
          <select data-testid="item-month-filter" value={month} onChange={(e) => setMonth(e.target.value)}
            className="w-full appearance-none bg-white rounded-full border border-riq-border pl-11 pr-9 py-3.5 text-sm outline-none focus:border-riq-primary cursor-pointer">
            <option value="all">All months</option>
            {months.map((m) => <option key={m.value} value={m.value}>{monthShort(m.value)} ({m.count})</option>)}
          </select>
          <CaretDown size={15} className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
        </div>
      </div>

      {month !== "all" && (
        <p data-testid="item-month-label" className="text-sm text-muted-foreground -mt-2">Showing what you bought in <span className="font-semibold text-riq-primary">{monthShort(month)}</span>.</p>
      )}

      <Card className="divide-y divide-riq-border p-0">
        {loadingMonth ? (
          <div className="px-5 py-10 text-center text-sm text-muted-foreground">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-muted-foreground">No items{month !== "all" ? ` in ${monthShort(month)}` : ""}.</div>
        ) : filtered.map((it) => (
          <div key={it.canonical_name} data-testid={`item-row-${it.canonical_name}`} onClick={() => navigate(`/items/${encodeURIComponent(it.canonical_name)}`)} className="w-full flex items-center gap-4 px-5 py-4 hover:bg-secondary/50 transition-colors cursor-pointer">
            <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${catColor(it.category)}22` }}><Basket size={18} weight="duotone" style={{ color: catColor(it.category) }} /></span>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm truncate">{it.canonical_name}</div>
              <div className="text-xs text-muted-foreground">{it.purchases} purchases · avg {money(it.average_price, cur)}{it.last_purchase ? ` · last ${monthShort(it.last_purchase)}` : ""}</div>
            </div>
            <div className="font-head font-semibold hidden sm:block">{money(it.total_spend, cur)}</div>
            <button data-testid={`track-toggle-${it.canonical_name}`} onClick={(e) => toggleTrack(e, it)} className="p-2"><Star size={20} weight={it.tracked ? "fill" : "regular"} className={it.tracked ? "text-riq-gold" : "text-neutral-300"} /></button>
            <CaretRight size={16} className="text-neutral-300" />
          </div>
        ))}
      </Card>
    </div>
  );
}
